import { useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import MarketplaceShell, { C, SERIF, card, pill } from "./MarketplaceShell";
import { SiteHeader } from "./SiteChrome";
import { useSiteStyles } from "./siteStyles";
import {
  CONTACT_LIMITS,
  ContactFormError,
  submitContactForm,
} from "../../services/publicContactApi";

/**
 * Contact, at /contact — built from the Claude Design file `Contact.dc.html`.
 *
 * <p>Posts to the pipeline that already existed: `POST /api/public/contact` validates and
 * rate-limits the submission, stores it, and emails council@. It is the same inbox an operator
 * reads at /admin/contact-requests, so nothing here is a new channel that nobody watches.</p>
 *
 * <p><b>A 200 means the message was STORED, not that an email went out.</b> The controller records
 * the submission whether or not the notification send succeeds, so the confirmation says "we have
 * it" rather than promising a delivery this page cannot observe.</p>
 *
 * <h3>Two deliberate departures from the design</h3>
 * <p><b>No topic is selected on load.</b> The design's own state starts on "Listing my business",
 * which is almost certainly there to show the selected styling — but shipped, it means every
 * enquiry from somebody who never touched the chips arrives subject-lined as a listing question,
 * including the billing problems and the complaints. The category is optional server-side, so
 * starting empty costs nothing and keeps the label truthful. Clicking a chosen chip clears it.</p>
 *
 * <p><b>The field captions are real &lt;label&gt; elements and the chips are buttons.</b> The design
 * draws both as styled divs and spans; they look identical this way, and a screen reader can
 * associate the captions while a keyboard can reach the chips.</p>
 */

/** The design's topics, in its order. */
const TOPICS = [
  "Listing my business",
  "My account or billing",
  "The DA data",
  "A problem with a listing",
  "Press",
  "Something else",
];

const CAPTION: CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 700,
  color: C.bodyMuted,
  marginBottom: 6,
};

const FIELD: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #d9d6cc",
  borderRadius: 10,
  padding: "12px 15px",
  fontSize: 14,
  fontFamily: "inherit",
  color: C.ink,
  outline: "none",
  background: "#fff",
};

const REQUIRED = <span style={{ color: "#b3261e" }}>*</span>;

/** One of the sidebar's white cards. */
function SideCard({ children }: { children: ReactNode }) {
  return <div style={{ ...card, padding: "24px 26px" }}>{children}</div>;
}

function MailIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.mint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="3" />
      <path d="m22 7-10 6L2 7" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.mint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function SideRow({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span
        style={{
          width: 30,
          height: 30,
          borderRadius: 9,
          background: "rgba(134,239,172,.15)",
          display: "grid",
          placeItems: "center",
          flex: "none",
        }}
      >
        {icon}
      </span>
      <span style={{ color: C.mintInk }}>{children}</span>
    </div>
  );
}

export default function PartnerNetworkContact() {
  // The site header's hover rules; the shell already loads the fonts.
  useSiteStyles("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [business, setBusiness] = useState("");
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The three the server requires, checked here only so somebody is not made to wait on a round
  // trip to be told a box is empty. Everything finer — the character rules, the length caps — is
  // left to the server and its message surfaced verbatim; a second copy of those rules here would
  // eventually disagree with the ones actually enforced.
  const canSend =
    name.trim().length > 0 && /\S+@\S+\.\S+/.test(email) && message.trim().length > 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSend || sending) return;

    setSending(true);
    setError(null);

    try {
      await submitContactForm({
        name: name.trim(),
        email: email.trim(),
        // Omitted rather than sent empty: the server's regexes accept "" but a blank string in an
        // operator's inbox reads as a field somebody cleared, not one they never filled in.
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(business.trim() ? { business: business.trim() } : {}),
        ...(topic ? { enquiryCategory: topic } : {}),
        message: message.trim(),
      });
      setSent(true);
    } catch (err) {
      setError(
        err instanceof ContactFormError
          ? err.message
          : "We couldn't send that just then. Please try again.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <MarketplaceShell
      metaTitle="Contact us"
      metaDescription="Get in touch with the DAconnect team about the marketplace, listing your business, or your account."
      eyebrow="CONTACT"
      title={
        <>
          {/* Mint, not the deep green the other pages use: the accent sits on a deep-green wash
              here, and deep-on-deep is the one combination in this palette that disappears. */}
          Send us a note — <b style={{ color: C.mint, fontWeight: 600 }}>we answer every message</b>.
        </>
      }
      intro="Questions about the marketplace, listing your business, or your account. Usually the same day."
      heroImage={{ src: "/images/partner-network/contact-hero.jpg" }}
      header={<SiteHeader active="contact" />}
    >
      <div className="dcx-split">
        {/* FORM */}
        <div style={{ ...card, padding: "32px 34px" }}>
          {sent ? (
            // Replaces the form rather than sitting above it. Leaving a filled-in form under a
            // success message invites a second identical submission.
            <div data-testid="contact-sent" role="status">
              <h2 style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 500, color: C.ink, margin: "0 0 8px" }}>
                Thanks — we've got your message.
              </h2>
              <p style={{ fontSize: 14.5, lineHeight: 1.7, color: C.bodyMuted, margin: "0 0 20px" }}>
                {/* No reply-time promise HERE. The hero and the sidebar carry the business's
                    "usually the same day"; repeating it against a specific message would read as a
                    commitment about that one, which nothing measures. */}
                Someone from the team will get back to you at{" "}
                <strong style={{ color: C.ink }}>{email.trim()}</strong>.
              </p>
              <Link to="/find" className="dcx-pill" style={{ ...pill, background: C.deep, color: "#fff" }}>
                Back to the marketplace
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} noValidate>
              {error && (
                // role=alert so it is announced: somebody who submitted with the keyboard may have
                // no idea anything appeared.
                <div
                  data-testid="contact-error"
                  role="alert"
                  style={{
                    background: "#fdf2f2",
                    border: "1px solid #f3c9c6",
                    borderRadius: 12,
                    padding: "12px 15px",
                    fontSize: 13.5,
                    color: "#8f231c",
                    marginBottom: 20,
                  }}
                >
                  {error}
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: "16px 18px" }}>
                <div>
                  <label htmlFor="contact-name" style={CAPTION}>YOUR NAME {REQUIRED}</label>
                  <input
                    id="contact-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={CONTACT_LIMITS.name}
                    autoComplete="name"
                    placeholder="e.g. Matt Hartley"
                    required
                    style={FIELD}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" style={CAPTION}>EMAIL {REQUIRED}</label>
                  <input
                    id="contact-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={CONTACT_LIMITS.email}
                    autoComplete="email"
                    placeholder="you@business.com.au"
                    required
                    style={FIELD}
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" style={CAPTION}>PHONE</label>
                  <input
                    id="contact-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    maxLength={CONTACT_LIMITS.phone}
                    autoComplete="tel"
                    placeholder="Optional"
                    style={FIELD}
                  />
                </div>
                <div>
                  <label htmlFor="contact-business" style={CAPTION}>BUSINESS</label>
                  <input
                    id="contact-business"
                    value={business}
                    onChange={(e) => setBusiness(e.target.value)}
                    maxLength={CONTACT_LIMITS.business}
                    autoComplete="organization"
                    placeholder="Optional"
                    style={FIELD}
                  />
                </div>
              </div>

              {/* TOPIC — real buttons in a fieldset, not styled spans, so the group can be reached
                  and operated from a keyboard. */}
              <fieldset style={{ border: 0, padding: 0, margin: "18px 0 0" }}>
                <legend style={{ ...CAPTION, marginBottom: 8, padding: 0 }}>WHAT'S IT ABOUT?</legend>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {TOPICS.map((t) => {
                    const on = topic === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        aria-pressed={on}
                        // Clicking the chosen one clears it: the field is optional, and a choice you
                        // cannot take back is a trap on a form with no "none of these".
                        onClick={() => setTopic(on ? "" : t)}
                        style={{
                          border: `1.5px solid ${on ? C.link : "#d9d6cc"}`,
                          background: on ? C.tint : "#fff",
                          color: on ? C.deep : C.bodyMuted,
                          borderRadius: 99,
                          padding: "8px 16px",
                          fontSize: 13,
                          fontWeight: 600,
                          fontFamily: "inherit",
                          cursor: "pointer",
                        }}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div style={{ marginTop: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                  <label htmlFor="contact-message" style={{ ...CAPTION, marginBottom: 0 }}>
                    MESSAGE {REQUIRED}
                  </label>
                  <span style={{ fontSize: 11.5, color: C.footInk }}>
                    {message.length} / {CONTACT_LIMITS.message}
                  </span>
                </div>
                <textarea
                  id="contact-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={CONTACT_LIMITS.message}
                  placeholder="Tell us what you need — the more detail the better."
                  required
                  style={{ ...FIELD, borderRadius: 12, padding: "14px 16px", minHeight: 150, resize: "vertical", lineHeight: 1.6 }}
                />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 18, flexWrap: "wrap" }}>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={!canSend || sending}
                  className="dcx-pill"
                  style={{
                    ...pill,
                    border: "none",
                    fontFamily: "inherit",
                    background: C.deep,
                    color: "#fff",
                    padding: "12px 28px",
                    cursor: canSend && !sending ? "pointer" : "not-allowed",
                    opacity: canSend && !sending ? 1 : 0.55,
                  }}
                >
                  {sending ? "Sending…" : "Send message"}
                </button>
                <span style={{ fontSize: 12.5, color: C.footInk }}>
                  We only use these details to reply to you.
                </span>
              </div>
            </form>
          )}
        </div>

        {/* SIDEBAR */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div
            data-testid="contact-photo-card"
            style={{
              borderRadius: 20,
              overflow: "hidden",
              position: "relative",
              minHeight: 170,
              border: `1px solid ${C.cardLine}`,
            }}
          >
            {/* Decorative, like the hero: the line written over it is the content, and the
                photograph is two people shaking hands in an office — not something worth
                describing, and not the tradesperson the design's own alt text claimed. */}
            <img
              src="/images/partner-network/contact-people.jpg"
              alt=""
              aria-hidden="true"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(0deg,rgba(20,83,45,.75),rgba(20,83,45,.05) 60%)",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: 20,
                right: 20,
                bottom: 16,
                color: "#fff",
                fontFamily: SERIF,
                fontSize: 17,
                fontWeight: 500,
                lineHeight: 1.35,
              }}
            >
              Real people answer — not a ticket queue.
            </div>
          </div>

          <div
            style={{
              background: C.deep,
              color: "#fff",
              borderRadius: 20,
              padding: "24px 26px",
              boxShadow: "0 20px 50px rgba(20,83,45,.18)",
            }}
          >
            <div style={{ fontFamily: SERIF, fontSize: 19, fontWeight: 500, marginBottom: 14 }}>
              Prefer email?
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, fontSize: 13.5 }}>
              <SideRow icon={<MailIcon />}>
                {/* The design draws this as plain text. It is an address — it should be clickable. */}
                <a href="mailto:council@daconnect.com.au" style={{ color: C.mintInk }}>
                  council@daconnect.com.au
                </a>
              </SideRow>
              <SideRow icon={<ClockIcon />}>Replies usually same business day</SideRow>
            </div>
          </div>

          <SideCard>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: C.ink, marginBottom: 10 }}>
              Before you write…
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 13, color: C.bodyMuted, lineHeight: 1.55 }}>
              {/* The design pointed these at #pricing, #how and #trust. All three pages exist. */}
              <div>· Pricing and plans are on the <Link to="/pricing" style={{ fontWeight: 700 }}>pricing page</Link>.</div>
              <div>· How the marketplace works, both sides: <Link to="/how-it-works" style={{ fontWeight: 700 }}>how it works</Link>.</div>
              <div>· Problem with a business you hired? See <Link to="/trust" style={{ fontWeight: 700 }}>trust &amp; safety</Link> first.</div>
            </div>
          </SideCard>

          <SideCard>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: C.ink, marginBottom: 6 }}>
              DAconnect Pty Ltd
            </div>
            <div style={{ fontSize: 13, color: C.bodyMuted, lineHeight: 1.6 }}>
              ABN 74 168 911 402
              <br />
              South Australia &amp; southern Queensland
            </div>
          </SideCard>
        </div>
      </div>
    </MarketplaceShell>
  );
}
